import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ConfirmModalComponent } from '../../../../shared/components/confirm-modal/confirm-modal.component';
import { ChemicalTemplateService, ChemicalTemplate } from '../../../../core/services/chemical-template.service';

@Component({
  selector: 'app-chemical-templates',
  standalone: true,
  imports: [CommonModule, ConfirmModalComponent],
  templateUrl: './chemical-templates.component.html',
  styleUrls: ['./chemical-templates.component.scss']
})
export class ChemicalTemplatesComponent implements OnInit {
  @ViewChild('confirmModal') confirmModal!: ConfirmModalComponent;

  private templateService = inject(ChemicalTemplateService);
  private router = inject(Router);

  // Use service signals
  templates = this.templateService.templates;
  isLoading = this.templateService.isLoading;
  error = this.templateService.error;

  templateToDelete: ChemicalTemplate | null = null;

  ngOnInit(): void {
    this.loadTemplates();
  }

  async loadTemplates(): Promise<void> {
    await this.templateService.loadTemplates();
  }

  addTemplate(): void {
    this.router.navigate(['/logbook/chemicals/templates/add']);
  }

  async useTemplate(template: ChemicalTemplate): Promise<void> {
    // Copy template to spray record
    const chemicals = await this.templateService.copyToSprayRecord(template.id);
    if (chemicals) {
      // Navigate to new spray record form with pre-filled chemicals
      this.router.navigate(['/logbook/spray-records/add'], {
        state: { chemicals }
      });
    }
  }

  viewTemplate(template: ChemicalTemplate): void {
    this.router.navigate(['/logbook/chemicals/templates', template.id, 'view']);
  }

  editTemplate(template: ChemicalTemplate): void {
    this.router.navigate(['/logbook/chemicals/templates', template.id, 'edit']);
  }

  deleteTemplate(template: ChemicalTemplate): void {
    this.templateToDelete = template;
    this.confirmModal.open({
      title: 'Delete Template',
      message: `Are you sure you want to delete "${template.name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmButtonClass: 'btn-danger',
      icon: 'bi-trash'
    });
  }

  async onDeleteConfirmed(): Promise<void> {
    if (this.templateToDelete) {
      const success = await this.templateService.deleteTemplate(this.templateToDelete.id);
      if (success) {
        console.log('Template deleted successfully');
      }
      this.templateToDelete = null;
    }
  }

  onDeleteCancelled(): void {
    this.templateToDelete = null;
  }

  async duplicateTemplate(template: ChemicalTemplate): Promise<void> {
    // Create a copy with "(Copy)" appended to name
    const duplicate = {
      name: `${template.name} (Copy)`,
      description: template.description,
      targetCrops: template.targetCrops,
      targetPests: template.targetPests,
      applicationTiming: template.applicationTiming,
      totalVolume: template.totalVolume,
      estimatedCostPerAcre: template.estimatedCostPerAcre,
      notes: template.notes,
      chemicals: template.items?.map(item => ({
        chemicalId: item.chemicalId || undefined,
        customChemicalId: item.customChemicalId || undefined,
        name: item.name,
        epaNumber: item.epaNumber,
        rate: item.rate,
        rateValue: item.rateValue,
        rateUnit: item.rateUnit,
        ratePerUnit: item.ratePerUnit,
        purpose: item.purpose
      })) || []
    };

    const result = await this.templateService.createTemplate(duplicate);
    if (result) {
      console.log('Template duplicated successfully');
    }
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }

  // Source badge helpers
  getSourceBadgeClass(source?: string | null): string {
    switch (source) {
      case 'epa':
        return 'bg-primary-subtle text-primary';
      case 'adn-recommended':
        return 'bg-success-subtle text-success';
      case 'custom':
        return 'bg-warning-subtle text-warning-emphasis';
      default:
        return 'bg-secondary-subtle text-secondary';
    }
  }

  getSourceLabel(source?: string | null): string {
    switch (source) {
      case 'epa':
        return 'EPA';
      case 'adn-recommended':
        return 'AA';
      case 'custom':
        return 'Custom';
      default:
        return 'N/A';
    }
  }
}
